import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  FileText, 
  Key, 
  CheckCircle2, 
  XCircle, 
  Clock,
  ArrowRight,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { api } from '@/utils/api';
import type { FileRecord } from '@/types';

interface Stats {
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  pendingFiles: number;
  apiKeys: number;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<Stats | null>(null);
  const [recentFiles, setRecentFiles] = useState<FileRecord[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const [filesResponse, apiKeysResponse] = await Promise.all([
        api.listFiles(),
        api.listApiKeys(),
      ]);
      
      const files = filesResponse.files;
      const apiKeys = apiKeysResponse.apiKeys;
      
      setStats({
        totalFiles: files.length,
        completedFiles: files.filter(f => f.status === 'COMPLETED').length,
        failedFiles: files.filter(f => f.status === 'FAILED' || f.status === 'TIMEOUT').length,
        pendingFiles: files.filter(f => ['PENDING', 'UPLOADING', 'QUEUED', 'PROCESSING'].includes(f.status)).length,
        apiKeys: apiKeys.length,
      });
      
      setRecentFiles(files.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: FileRecord['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'FAILED':
      case 'TIMEOUT':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-amber-500" />;
    }
  };

  const getStatusBadge = (status: FileRecord['status']) => {
    switch (status) {
      case 'COMPLETED':
        return <Badge variant="default" className="bg-green-500 hover:bg-green-600">Completed</Badge>;
      case 'FAILED':
        return <Badge variant="destructive">Failed</Badge>;
      case 'TIMEOUT':
        return <Badge variant="destructive">Timed out</Badge>;
      case 'PENDING':
      case 'UPLOADING':
      case 'QUEUED':
      case 'PROCESSING':
        return <Badge variant="secondary" className="bg-amber-500 text-white hover:bg-amber-600">Processing</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: number;
    icon: React.ReactNode;
    description?: string;
    onClick?: () => void;
    variant?: 'default' | 'success' | 'error' | 'warning';
  }> = ({ title, value, icon, description, onClick, variant = 'default' }) => {
    const variantStyles = {
      default: 'border-l-4 border-l-primary',
      success: 'border-l-4 border-l-green-500',
      error: 'border-l-4 border-l-red-500',
      warning: 'border-l-4 border-l-amber-500',
    };

    return (
      <Card 
        className={`${variantStyles[variant]} ${onClick ? 'cursor-pointer hover:shadow-md transition-shadow' : ''}`}
        onClick={onClick}
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{title}</p>
              <p className="text-3xl font-bold mt-2">
                {loading ? '-' : value}
              </p>
              {description && (
                <p className="text-xs text-muted-foreground mt-1">{description}</p>
              )}
            </div>
            <div className="p-3 bg-primary/10 rounded-full">
              {icon}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Secure document sanitization overview
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Files"
          value={stats?.totalFiles || 0}
          icon={<FileText className="h-6 w-6 text-primary" />}
          description="All processed files"
          onClick={() => navigate('/files')}
        />
        <StatCard
          title="Completed"
          value={stats?.completedFiles || 0}
          icon={<CheckCircle2 className="h-6 w-6 text-green-500" />}
          description="Successfully sanitized"
          variant="success"
          onClick={() => navigate('/files')}
        />
        <StatCard
          title="Failed"
          value={stats?.failedFiles || 0}
          icon={<XCircle className="h-6 w-6 text-red-500" />}
          description="Processing errors"
          variant="error"
          onClick={() => navigate('/files')}
        />
        <StatCard
          title="API Keys"
          value={stats?.apiKeys || 0}
          icon={<Key className="h-6 w-6 text-amber-500" />}
          description="Active API access keys"
          variant="warning"
          onClick={() => navigate('/apikeys')}
        />
      </div>

      <div className="grid gap-8 lg:grid-cols-3">
        {/* Recent Files */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Recent Files</CardTitle>
            <CardDescription>Latest file processing activity</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                ))}
              </div>
            ) : recentFiles.length > 0 ? (
              <div className="space-y-4">
                {recentFiles.map((file, index) => (
                  <div key={file.fileId}>
                    <div className="flex items-center justify-between py-2">
                      <div className="flex items-center gap-4">
                        {getStatusIcon(file.status)}
                        <div>
                          <p className="font-medium truncate max-w-[300px]">
                            {file.originalFilename}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(file.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(file.status)}
                    </div>
                    {index < recentFiles.length - 1 && <Separator />}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No files yet. Start by uploading a file!</p>
              </div>
            )}
            <Button 
              variant="outline" 
              className="w-full mt-6"
              onClick={() => navigate('/files')}
            >
              View All Files
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>

        {/* Quick Actions & Info */}
        <div className="space-y-6">
          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-start" 
                onClick={() => navigate('/upload')}
              >
                <Upload className="mr-2 h-4 w-4" />
                Upload New File
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/files')}
              >
                <FileText className="mr-2 h-4 w-4" />
                Browse Files
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={() => navigate('/apikeys')}
              >
                <Key className="mr-2 h-4 w-4" />
                Manage API Keys
              </Button>
            </CardContent>
          </Card>

          {/* Security Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                About Undanger
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Undanger uses pixel-based rendering technology to securely convert 
                potentially malicious documents into safe PDFs. All executable code 
                is removed while preserving visual content.
              </p>
              <div className="flex items-start gap-3 text-sm">
                <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
                <p className="text-muted-foreground">
                  Files are processed securely and automatically deleted after 7 days.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
